<template>
  <div class="video-container">
    
    <video
      v-for="(src, index) in videoList"
      :key="index"
      :src="src"
      :autoplay="index === currentIndex && config.autoPlay"
      :muted="muteVideo"
      @ended="handleEnded"
      @loadeddata="onVideoLoaded(index)"
      @loadstart="onVideoLoadStart(index)"
      @error="handleVideoError"
      class="video-fill"
      :style="{
        visibility: index == currentIndex ? 'visible' : 'hidden',
        zIndex: index === currentIndex ? 2 : 1,
        position: 'absolute',
        top: 0,
        left: 0,
      }"
      :class="{ 'video-hidden': !videoLoaded[index] }"
     
      ref="videoRefs"
    />
    <div v-if="!videoLoaded[currentIndex]" class="loading-indicator">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "MultiVideoWidget",
};
</script>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  nextTick,
  defineProps,
  onMounted,
  onBeforeUnmount,
} from "vue";

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  queueAudioPlaying: {
    type: Boolean,
    required: true,
  },
});

const videoRefs = ref<HTMLVideoElement[]>([]);
const videoList = computed(() => props.config.src);
const videoLoaded = ref<boolean[]>([]);
const currentIndex = ref(0);
const isActuallyMuted = ref(false);
let unmuteTimeout: number | null = null;

// Mute logic
watch(
  () => props.queueAudioPlaying,
  (newVal) => {
    if (newVal) {
      // mute when audio starts
      isActuallyMuted.value = true;
      if (unmuteTimeout) {
        clearTimeout(unmuteTimeout);
        unmuteTimeout = null;
      }
    } else {
      // unmute after 1 second when audio stops
      unmuteTimeout = window.setTimeout(() => {
        isActuallyMuted.value = false;
        unmuteTimeout = null;
      }, 1000);
    }
  },
  { immediate: true }
);

const muteVideo = computed(() => {
  if (props.config.muted) return true;
  return isActuallyMuted.value;
});

function onVideoLoaded(index: number) {
  videoLoaded.value[index] = true;
}

function onVideoLoadStart(index: number) {
  videoLoaded.value[index] = false;
}

function handleVideoError() {
  console.error("Error loading video:", videoList.value[currentIndex.value]);
  videoLoaded.value[currentIndex.value] = false;

  //move to next vid
  if (videoList.value && videoList.value.length > 0) {
    if (currentIndex.value < videoList.value.length - 1) {
      currentIndex.value += 1;
    } else if (props.config.loop) {
      currentIndex.value = 0;
    }

    // play next vid if autoplay is true
    if (props.config.autoPlay && videoRefs.value[currentIndex.value]) {
      nextTick(() => videoRefs.value[currentIndex.value]?.play());
    }
  }
}

function handleEnded() {
  if (!videoList.value || videoList.value.length === 0) return;

  if (props.config.loop) {
    currentIndex.value = (currentIndex.value + 1) % videoList.value.length;
  } else if (currentIndex.value < videoList.value.length - 1) {
    currentIndex.value += 1;
  }


  //pause prev video
  const prevVideo = videoRefs.value[currentIndex.value];
  if (prevVideo && !prevVideo.paused) {
    prevVideo.pause();
  }

  if (props.config.autoPlay && videoRefs.value[currentIndex.value]) {
    nextTick(() => videoRefs.value[currentIndex.value]?.play());
  }
}

onBeforeUnmount(() => {
  if (unmuteTimeout) clearTimeout(unmuteTimeout);
});
</script>

<style scoped>
.video-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.video-fill {
  width: 100%;
  height: 100%;
  object-fit: unset;
  display: block;
}

.video-hidden {
  opacity: 0;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

